import { 
  Card, 
  CardContent, 
  CardHeader, 
  Chip, 
  Divider, 
  Grid, 
  Stack, 
  Typography,
  IconButton,
  Snackbar,
  Alert
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from "dayjs";
import { VacationType, FirstDay, TeamLabel } from '../../shares/Tools';
import { useParams } from 'react-router';
import { TeamForm } from './TeamForm';
import { useUpdateTeamMutation, useDeleteTeamMutation, useUpdateTeamStatusMutation } from '../../store/team/TeamApi';
import React from "react";
import { ConfirmDialog } from "../../shares/ConfirmDialog";
import { myConstants } from "../../shares/Constants";
import { CheckCircleOutlined, RemoveCircleOutlined } from "@mui/icons-material";


/*
 * Die Farbe der Teams richtet sich
 * nach dem Status der Anmeldung
 */ 
const STATUS_COLOR = 
{
  registered: 'success',
  waitlist: 'warning',
  cancelled: 'default', 
};


/*
 * Bezeichnung des aktuellen Status
 * eines Teams. 
 */
const STATUS_LABEL = 
{
  registered: 'Angemeldet',
  waitlist: 'Warteliste',
  cancelled: 'Zurückgezogen', 
};


export default function TeamDetail({ team, club }) 
{

  const { year } = useParams();
  const [isEditing, setIsEditing] = React.useState(false);
  // Kennzeichen, ob der Lösch-Bestätigungsdialog offen ist oder nicht
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState(false);

  const [updateTeam, { isLoading : isUpdating }] = useUpdateTeamMutation();
  const [deleteTeam, { isLoading : isDeleting }] = useDeleteTeamMutation();
  const [updateTeamStatus] = useUpdateTeamStatusMutation();

  // sicherstellen, dass wir auch wirklich ein Team haben
  if (!team){
    return null;
  } 

  // Bearbeiten des Löschens (sehr vereinfacht,
  // da das meiste direkt in dem Dialog durchgeführt
  // wird):
  const teamStatus = team.status ?? myConstants.teamModel.status.registered;
  const handleDelete = () => setConfirmOpen(true);


  /*
   * Behandlung des Zurückziehens eines Teams.
  */
  const handleWithdraw = async () => {
    try 
    {
      await updateTeamStatus(
        {
          year, 
          clubLabel:  team.clubLabel,
          teamLabel:  team.label,
          teamCounter:team.counter,
          status:     myConstants.teamModel.status.withdrawn,
        }
      ).unwrap();
    }
    catch (error)
    {
      console.error('Fehler beim Zurückziehen ', error);
    }
  };

  /*
   * Behandlung des Reaktivieren eines Teams.
  */
  const handleReactivate = async () => {
    try 
    {
      await updateTeamStatus(
        {
          year, 
          clubLabel:  team.clubLabel,
          teamLabel:  team.label,
          teamCounter:team.counter,
          status:     myConstants.teamModel.status.registered,
        }
      ).unwrap();
    }
    catch (error)
    {
      if (error.status === 409)
      {
        setErrorMsg(error.data?.error ?? 'Kapazität ist ausgeschöpft.');
      }
      else 
      {
        console.error('Fehler beim Reaktivieren ', error);
      }
    }
  };



  // Bearbeiten des Updates
  const handleUpdate = async (formData) =>
  {
    try
    {
      await updateTeam(
        {
          year, 
          clubLabel   : team.clubLabel,
          teamLabel   : team.label,
          teamCounter : team.counter, 
          ...formData,
        }
      ).unwrap();
      setIsEditing(false);
    }
    catch (error)
    {
      console.error('Fehler beim Aktualisieren des Teams: ', error);
    }
  };


  // Initialisierung der Werte aus den gespeicherten Daten
  const initialValues = 
  {
    players:                    team.players,
    fellows:                    team.fellows,
    vacationType:               team.vacationType,
    firstDay:                   team.firstDay,
    saturdayBreakfast:          team.saturdayBreakfast,
    sundayBreakfast:            team.sundayBreakfast,
    saturdayLunch:              team.saturdayLunch,
    barSets:                    team.barSets,
    shuttleArrivalPassengers:   team.shuttleArrivingCount,
    shuttleDeparturePassengers: team.shuttleDepartingCount,
    shuttleArrivalDate:         team.shuttleArrivingDate,
    shuttleDepartureDate:       team.shuttleDepartingDate,
  };

  // Prüfen, ob irgendein Essen angefragt wurde - ansonsten zeigen wir das alles nicht an.
  const hasFood = 
    (
         team.fridayLunch > 0 
      || team.saturdayBreakfast > 0 
      || team.saturdayLunch > 0 
      || team.sundayBreakfast > 0
    );

  // Prüfen, ob es ein angefragt wurde  
  const hasShuttle = team.shuttleArrivingCount > 0 || team.shuttleDepartingCount > 0;

  
  return (
    <Card id={`team-${team.id}`} sx={{ mb: 2, scrollMarginTop: '80px' }} variant="outlined">
      <CardHeader
        title={TeamLabel(team.label, team.counter)}
        subheader={!isEditing ? VacationType(team.vacationType) : undefined}
        titleTypographyProps={{ variant: 'h5' }}
        action={!isEditing && (
          <>
            <Chip 
              size="small"
              label={STATUS_LABEL[teamStatus]}
              color={STATUS_COLOR[teamStatus]}
              sx={{ mr:1, mt:0.5, verticalAlign: 'middle'}}
            />
            {
              teamStatus !== myConstants.teamModel.status.withdrawn 
              ? (
                <IconButton 
                  onClick={handleWithdraw} 
                  aria-label="Zurückziehen" 
                  title="Zurückziehen" 
                  color={STATUS_COLOR[myConstants.teamModel.status.withdrawn]}
                >
                  <RemoveCircleOutlined />
                </IconButton>
              )
              : (
                <IconButton 
                  onClick={handleReactivate} 
                  aria-label="Reaktivieren" 
                  title="Reaktivieren" 
                  color={STATUS_COLOR[myConstants.teamModel.status.registered]}
                >
                  <CheckCircleOutlined />
                </IconButton>
              )
            }
            {
              teamStatus === myConstants.teamModel.status.withdrawn
              && (
                <IconButton onClick={handleDelete} aria-label="Löschen" disabled={isDeleting}>
                  <DeleteIcon />
                </IconButton>
              )
            }
            <IconButton onClick={() => setIsEditing(true)} aria-label="Bearbeiten">
              <EditIcon />
            </IconButton>
          </>  
        )}
      />
      <CardContent>
        {isEditing ? (
          <TeamForm
            year={year}
            club={club}
            initialValues={initialValues}
            onSubmit={handleUpdate}
            isLoading={isUpdating}
            onCancel={() => setIsEditing(false)}
            submitLabel="Speichern"
          />
        ) : (
          <Grid container spacing={2}>
            <Grid size={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Personen</Typography>
              <Typography>{team.players} SpielerInnen · {team.fellows} Mitreisende</Typography>
            </Grid>

            {team.vacationType === 'overnight' && (
              <Grid size={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Anreisetag</Typography>
                <Typography>{FirstDay( team.firstDay )}</Typography>
              </Grid>
            )}

            <Grid size={12}>
              <Divider sx={{ my: 1 }}><Typography variant="overline">Verpflegung</Typography></Divider>
              {hasFood ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {team.saturdayBreakfast > 0 && <Chip label={`Sa. Frühstück: ${team.saturdayBreakfast}`} size="small" />}
                  {team.saturdayLunch > 0 && <Chip label={`Sa. Abendessen: ${team.saturdayLunch}`} size="small" />}
                  {team.sundayBreakfast > 0 && <Chip label={`So. Frühstück: ${team.sundayBreakfast}`} size="small" />}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Keine Verpflegung gebucht</Typography>
              )}
            </Grid>

            {team.barSets > 0 && (
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Biergarnituren</Typography>
                <Typography>{team.barSets} Garnitur{team.barSets !== 1 ? 'en' : ''}</Typography>
              </Grid>
            )}

            {hasShuttle && (
              <Grid size={12}>
                <Divider sx={{ my: 1 }}><Typography variant="overline">Shuttle</Typography></Divider>
                <Stack spacing={1}>
                  {
                    team.shuttleArrivingCount > 0 
                    && (
                      <Typography>Ankunft: 
                        {
                          team.shuttleArrivingCount} Person{
                          team.shuttleArrivingCount !== 1 ? 'en' : ''} · {
                          dayjs(team.shuttleArrivingDate).format('DD.MM.YYYY HH:mm')
                        } Uhr
                      </Typography>
                    )
                  }
                  {
                    team.shuttleDepartingCount > 0 
                    && (
                      <Typography>
                        Abfahrt: {team.shuttleDepartingCount} 
                        Person{team.shuttleDepartingCount !== 1 ? 'en' : ''} · 
                        { dayjs(team.shuttleDepartingDate).format('DD.MM.YYYY HH:mm') } Uhr
                      </Typography>
                    )
                  }
                </Stack>
              </Grid>
            )}

            <Grid size={12}>
              <Divider sx={{ my: 1 }}><Typography variant="overline">Zahlung</Typography></Divider>
              <Stack direction="row" spacing={3}>
                <Typography>Fällig: EUR {parseFloat(team.vacationFee).toFixed(2)}</Typography>
                <Typography>Bezahlt: EUR {parseFloat(team.feePayedSum).toFixed(2)}</Typography>
                {dayjs(team.feePayedDate).year() > 1 && (
                  <Typography color="text.secondary">am {dayjs(team.feePayedDate).format('DD.MM.YYYY')}</Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
      </CardContent>

      <ConfirmDialog
        open  = {confirmOpen}
        title = "Team löschen"
        text  = {`${TeamLabel(team.label, team.counter)} wirklich permanent löschen?`}
        onConfirm= {
          async () => {
            try 
            {
              await deleteTeam({year, clubLabel : team.clubLabel, teamLabel : team.label, teamCounter : team.counter}).unwrap();
            }
            catch (error)
            {
              console.error("Fehler beim Löschen: ", error);
            }
            setConfirmOpen(false);
          }
        }
        onCancel = {
          () => setConfirmOpen(false)
        } 
      />

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg('')}
      >
        <Alert severity="error" onClose={ () => setErrorMsg('')} variant="filled">
          {errorMsg}
        </Alert>
      </Snackbar> 
    </Card>
  );
}